import type { Proforma } from "@/lib/proforma.api";
import { STATUS_LABELS, type ProformaStatusFilter } from "./proforma.constants";

export interface ProformaMetrics {
  totalValue: number;
  activeCount: number;
  expiredCount: number;
  cancelledCount: number;
}

export function getProformaTotal(proforma: Proforma): number {
  return proforma.lines.reduce((sum, line) => sum + parseFloat(line.lineTotal), 0);
}

export function getProformaSummary(proforma: Proforma): string {
  const ticketCount = proforma.lines.filter((line) => line.lineType === "ticket").length;
  const shopCount = proforma.lines.filter((line) => line.lineType === "shop").length;
  const parts = [
    `${proforma.lines.length} ligne${proforma.lines.length > 1 ? "s" : ""}`,
    ticketCount > 0 ? `${ticketCount} billet${ticketCount > 1 ? "s" : ""}` : "",
    shopCount > 0 ? `${shopCount} shop` : "",
  ].filter(Boolean);

  return parts.join(" · ");
}

export function sortProformasByNewest(proformas: Proforma[]): Proforma[] {
  return [...proformas].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function getProformaMetrics(proformas: Proforma[]): ProformaMetrics {
  return {
    totalValue: proformas.reduce((sum, proforma) => sum + getProformaTotal(proforma), 0),
    activeCount: proformas.filter(
      (proforma) => proforma.status === "draft" || proforma.status === "issued",
    ).length,
    expiredCount: proformas.filter((proforma) => proforma.status === "expired").length,
    cancelledCount: proformas.filter((proforma) => proforma.status === "cancelled").length,
  };
}

export function filterProformas(
  proformas: Proforma[],
  search: string,
  statusFilter: ProformaStatusFilter,
): Proforma[] {
  const needle = search.trim().toLowerCase();

  return proformas.filter((proforma) => {
    const matchesStatus = statusFilter === "all" || proforma.status === statusFilter;
    if (!matchesStatus) return false;
    if (!needle) return true;

    return [
      proforma.number,
      STATUS_LABELS[proforma.status],
      proforma.partySnapshot?.fullName,
      proforma.partySnapshot?.phone,
      proforma.partySnapshot?.email,
      ...proforma.lines.flatMap((line) => [
        line.description,
        line.lineType,
        line.ticketDetails?.passengerName,
        line.shopDetails?.passengerName,
      ]),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  });
}
